/* RESPONSIBLE TEAM: team-reporting */

import { use } from 'ember-resources/util/function-resource';
import { getOwner, setOwner } from '@ember/application';
import { getNumericValue } from './chart-data-resource-compatible';
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import type ReportingChartService from 'embercom/services/reporting-chart-service';

class FinGettingStartedState {
  @service declare appService: AppService;
  @service declare reportingChartService: ReportingChartService;

  constructor(owner: object) {
    setOwner(this, getOwner(owner));
  }

  @use involvementRateResource = staticDataResource(this, {
    serializedChart: () => ({
      visualization_type: 'counter',
      chart_series: [{ metric_id: 'fin.participation_rate' }],
      date_range: {
        selection: 'past_12_weeks',
      },
    }),
  });

  @use conversationsWithQueryTypeResource = staticDataResource(this, {
    serializedChart: () => ({
      visualization_type: 'counter',
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'exists',
            data: {
              property: 'conversation_custom_fields#ai_insights_query_type',
              attribute: 'conversation_custom_fields#ai_insights_query_type',
            },
          },
        },
      ],
      date_range: {
        selection: 'past_12_months',
      },
    }),
  });

  async withLocalStorageCache<T>(key: string, fn: () => Promise<T>): Promise<T> {
    let appId = this.appService.app.id;
    let cacheKey = `${appId}:gettingStarted:${key}`;
    let cachedValue = localStorage.getItem(cacheKey);
    if (cachedValue) {
      let { expiresAt, value } = JSON.parse(cachedValue);
      if (new Date(expiresAt) > new Date()) {
        return Promise.resolve(value);
      }
    }
    let value = await fn();
    let expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24); // 1 day from now
    localStorage.setItem(cacheKey, JSON.stringify({ expiresAt, value }));
    return value;
  }

  get involvementRate() {
    let metricValue = this.involvementRateResource?.kpiData?.[0];
    return getNumericValue(metricValue) || 0;
  }

  get conversationsWithQueryType() {
    let metricValue = this.conversationsWithQueryTypeResource?.kpiData?.[0];
    return getNumericValue(metricValue) || 0;
  }

  async getState() {
    if (this.reportingChartService.syntheticDataUseCase) {
      return 'personalized';
    }

    return this.withLocalStorageCache('state', async () => {
      // accessing a staticDataResource will lazily perform the loadAllData task,
      // so let's wait for it to finish first
      await taskFor(this.involvementRateResource.loadAllData).last;
      if (this.involvementRate < 15) {
        // we only need to access this resource if the involvement rate is below the threshold
        await taskFor(this.conversationsWithQueryTypeResource.loadAllData).last;
        if (this.conversationsWithQueryType > 1000) {
          return 'personalized';
        }
        return 'generic';
      }
      return null; // don't show anything
    });
  }
}

type FinGettingStartedStates = Awaited<InstanceType<typeof FinGettingStartedState>['getState']>;

export { FinGettingStartedState, FinGettingStartedStates };
